import { useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { type Identifier, useGetList } from "ra-core";

import type { ZoomMeetingSummaryView } from "../types";
import {
  meetingClockRef,
  useRecordings,
} from "../zoom_meeting_summaries/RecordingPlayer";
import {
  ASSET_KINDS,
  type AssetKind,
  assetState,
  refetchWhileAwaiting,
  rowsFromQueryData,
} from "./meetingAssets";

/**
 * A meeting's assets as the meeting page sees them: per-asset state from the
 * same view row the list reads, so the two can never disagree, plus the
 * recording rows that carry the links and transcript text.
 *
 * `getOne("zoom_meetings")` reads the base table on purpose (the edit form
 * writes back to it), so the view row is fetched as a one-row list, which the
 * data provider sends to zoom_meetings_summary.
 *
 * The row polls while anything is waiting or uploading. When a state changes,
 * the recordings are refetched too: useRecordings stops its own polling at the
 * first media row, even one still uploading, so without this a link that lands
 * later would wait for a reload.
 *
 * Known limit: a second track of a kind that is already available (a gallery
 * view after active speaker) changes no state, so it appears on reload. Zoom
 * delivers a meeting's layouts in one batch.
 */
export function useMeetingAssets(id: Identifier | undefined) {
  const { data } = useGetList<ZoomMeetingSummaryView>(
    "zoom_meetings",
    {
      filter: { "id@eq": id },
      pagination: { page: 1, perPage: 1 },
      sort: { field: "id", order: "ASC" },
    },
    {
      enabled: id != null,
      refetchInterval: (query) =>
        refetchWhileAwaiting(
          rowsFromQueryData<ZoomMeetingSummaryView>(query.state.data),
        ),
    },
  );
  const row = data?.[0];

  const { data: recordings } = useRecordings(
    id as number | undefined,
    row ? meetingClockRef(row) : null,
  );

  const now = Date.now();
  const state = (kind: AssetKind) =>
    row ? assetState(row, kind, now) : "none";

  // Joined from the view's own columns, not from `state`, so the clock ticking
  // a "waiting" into "none" does not count as a change worth refetching for.
  const signature = row
    ? [
        row.screen_state,
        row.camera_state,
        row.audio_state,
        row.transcript_state,
        row.summary_count,
      ].join("|")
    : null;
  const queryClient = useQueryClient();
  const previous = useRef<string | null>(null);
  useEffect(() => {
    if (signature == null) return;
    if (previous.current != null && previous.current !== signature) {
      queryClient.invalidateQueries({
        queryKey: ["zoom_recordings", "getList"],
      });
    }
    previous.current = signature;
  }, [signature, queryClient]);

  return { row, recordings, state, kinds: ASSET_KINDS };
}

export type MeetingAssets = ReturnType<typeof useMeetingAssets>;
